import { API_ENDPOINT } from 'src/config/env.config';
import { baseApi } from './axios';
import {
	BaseAPIResponse,
	ConnectUserForPositionInput,
	ConfirmLevelInput,
	GetLevelDistributionDataAPIResponse,
	GetPositionByIdAPIResponse,
	GetPositionsAPIResponse,
	GetPositionsForRecruiterAPIResponse,
	RegisterPosition,
	RegisterPositionAPIResponse,
	UpdateRecommendedFlag,
	GetPositionUserRecommendationStatusAPIResponse,
	FilterRecommendationsInput,
	GetRecommendationsForRecruiterAPIResponse,
	FilterPositionCompanyInput,
	InviteCollaboratorForPositionInput,
	UpdatePositionExpiryByIdInput,
	UpdateApplicationExpiryByIdInput,
	UpdateAutoRecommendationExpiryByIdInput,
	GeneratePositionJDInput,
	GetPositionJDAPIResponse,
	PositionStatus,
	CreatePositionRequestV2,
	UIPosition,
	GetUsersListForARoundApiResponse,
	UserExperienceLevelType,
	UserFiltersForApis,
} from 'types';

const baseUrl = `${API_ENDPOINT}/position`;
export const getPositions = async (): Promise<GetPositionsAPIResponse> => {
	const response = await baseApi.get(`${baseUrl}/all`);
	return response.data;
};

export const getMyRegisteredPositions = async (
	args: FilterPositionCompanyInput & { companyId?: string, allPosition?: boolean, myPosition?: boolean, positionStatus?: string, page?: number, limit?: number },
): Promise<GetPositionsForRecruiterAPIResponse> => {
	const response = await baseApi.get(`${baseUrl}/my`, {
		params: {
			...args,
			allPosition: args.allPosition,
			myPosition: args.myPosition,
			positionStatus: args.positionStatus,
			page: args.page,
			limit: args.limit,

		},
	});
	return response.data;
};

export const getRecommendationStatus = async (args: {
	positionId: string;
	handler: string;
}): Promise<GetPositionUserRecommendationStatusAPIResponse> => {
	const response = await baseApi.get(`${baseUrl}/recommendation-status/${args.positionId}/${args.handler}`);
	return response.data;
};

export const getPositionById = async (id: string): Promise<GetPositionByIdAPIResponse> => {
	const response = await baseApi.get(`${baseUrl}/${id}`);
	return response.data;
};

export const getPositionDetailsById = async (id: string): Promise<GetPositionByIdAPIResponse> => {
	const response = await baseApi.get(`${baseUrl}/job/${id}`);
	return response.data;
};

export const registerNewPosition = async (payload: RegisterPosition): Promise<RegisterPositionAPIResponse> => {
	const response = await baseApi.post(`${baseUrl}/new`, payload);
	return response.data;
};

export const generatePositionJDFn = async (payload: GeneratePositionJDInput): Promise<GetPositionJDAPIResponse> => {
	const response = await baseApi.post(`${baseUrl}/generate-JD`, payload);
	return response.data;
};

export const connectToAUser = async (payload: ConnectUserForPositionInput): Promise<BaseAPIResponse> => {
	const response = await baseApi.post(`${baseUrl}/connect`, payload);
	return response.data;
};
export const editPosition = async (args: { id: string; payload: RegisterPosition }): Promise<BaseAPIResponse> => {
	const response = await baseApi.post(`${baseUrl}/edit/${args.id}`, args.payload);
	return response.data;
};

export const levelDistribution = async (id: string): Promise<GetLevelDistributionDataAPIResponse> => {
	const response = await baseApi.get(`${baseUrl}/level-distribution/${id}`);
	return response.data;
};

export const confirmLevelFn = async (args: ConfirmLevelInput): Promise<BaseAPIResponse> => {
	const response = await baseApi.post(`${baseUrl}/confirm-level`, args);
	return response.data;
};

export const getMIAClosureEligibility = async (
	positionId: string,
): Promise<BaseAPIResponse & { hasMIAEligibleApplicants?: boolean; count?: number }> => {
	const response = await baseApi.get(`${baseUrl}/miaClosureEligibility/${positionId}`);
	return response.data;
};

export const closePosition = async (args: {
	id: string;
	reason: string;
	otherReason?: string;
}): Promise<BaseAPIResponse> => {
	const response = await baseApi.post(`${baseUrl}/closePosition/${args.id}`, {
		reason: args.reason,
		otherReason: args.otherReason,
	});
	return response.data;
};

export const reopenPosition = async (args: { id: string }): Promise<BaseAPIResponse> => {
	const response = await baseApi.post(`${baseUrl}/reopenPosition/${args.id}`, { status: PositionStatus.OPEN });
	return response.data;
};

export const updateShowRecommendedFlagByRecruiter = async (args: {
	id: string;
	payload: UpdateRecommendedFlag;
}): Promise<BaseAPIResponse> => {
	const response = await baseApi.patch(`${baseUrl}/update-show-recommended-flag/${args.id}`, { ...args.payload });
	return response.data;
};

export const completeLevelExplainer = async (args: { positionId: string }): Promise<BaseAPIResponse> => {
	const response = await baseApi.post(`${baseUrl}/complete-level-explainer`, args);
	return response.data;
};

export const getRecommendationsForPosition = async (
	args: FilterRecommendationsInput & { positionId: string },
): Promise<GetRecommendationsForRecruiterAPIResponse> => {
	const response = await baseApi.get(`${baseUrl}/recommendations/${args.positionId}`, { params: args });
	return response.data;
};
export const completeGuidedTour = async (args: { positionId: string }): Promise<BaseAPIResponse> => {
	const response = await baseApi.post(`${baseUrl}/complete-guided-tour`, args);
	return response.data;
};

export const requestFurtherRecommendations = async (args: { positionId: string }): Promise<BaseAPIResponse> => {
	const response = await baseApi.patch(`${baseUrl}/request-further-recommendations`, args);
	return response.data;
};

export const inviteCollaboratorForPosition = async (
	args: InviteCollaboratorForPositionInput,
): Promise<BaseAPIResponse> => {
	console.log('args:', args);
	const response = await baseApi.post(`${baseUrl}/invite-collaborator`, args);
	return response.data;
};

export const updatePositionExpiryById = async (args: UpdatePositionExpiryByIdInput): Promise<BaseAPIResponse> => {
	const response = await baseApi.patch(`${baseUrl}/update-expiry/${args?.positionId}`, args);
	return response.data;
};

export const updateApplicationExpiryById = async (args: UpdateApplicationExpiryByIdInput): Promise<BaseAPIResponse> => {
	const response = await baseApi.patch(`${baseUrl}/update-application-expiry/${args?.positionId}`, args);
	return response.data;
};

export const updateAutoRecommendationExpiryById = async (args: UpdateAutoRecommendationExpiryByIdInput): Promise<BaseAPIResponse> => {
	const response = await baseApi.patch(`${baseUrl}/update-auto-recommendation-expiry/${args?.positionId}`, args);
	return response.data;
};

export const createSlackChannelByPositionId = async ({
	positionId,
	workspaceIds,
}: {
	positionId: string;
	workspaceIds?: string[];
}): Promise<BaseAPIResponse> => {
	const response = await baseApi.post(`${baseUrl}/create-slack-channel/${positionId}`, {
		workspaceIds,
	});
	return response.data;
};

export const downloadData = async ({
	positionId,
	downloadType,
}: {
	positionId: string;
	downloadType: string;
}): Promise<BaseAPIResponse> => {
	const response = await baseApi.get(`${baseUrl}/download-data/${positionId}?downloadType=${downloadType}`);
	return response.data;
};

export const createPositionV2 = async (
	data: CreatePositionRequestV2,
): Promise<BaseAPIResponse & { position?: UIPosition }> => {
	const response = await baseApi.post(`${baseUrl}/createPositionV2`, data);
	return response.data;
};

export const getHiringCandidatesForPosition = async (
	data: UserFiltersForApis & {
		page: number;
		limit?: number;
		positionId?: string;
	},
): Promise<GetUsersListForARoundApiResponse> => {
	const response = await baseApi.post(`${baseUrl}/getAllRecommendedCandidates`, data);
	return response.data;
};

export const getHiringApplicantsForPosition = async (
	data: UserFiltersForApis & {
		page: number;
		limit?: number;
		positionId: string;
	},
): Promise<GetUsersListForARoundApiResponse> => {
	const response = await baseApi.post(`${baseUrl}/getAllApplicantsForPosition`, data);
	return response.data;
};

export const getWorkspacesForEnvironment = async (): Promise<{
	success: boolean;
	workspaces: Array<{ workspaceId: string, workspaceName: string, environment?: string }>;
}> => {
	const response = await baseApi.get(`${baseUrl}/workspaces-for-environment`);
	return response.data;
};
